// BHP-1191: what the customer sees after paying.
//
// Public, and that is the whole point: they clicked a button in an invoice
// mail and are not logged in, so they must not land on a sign-in screen.
// Rendered outside the auth providers (see index.tsx), so nothing here waits
// on Authentik — someone who has just paid should never be looking at a
// spinner because our identity provider is slow.
//
// Uses AuthLayout, the same shell as the login screen (BHP-1038): this is the
// other page a stranger can reach, and it should look like it belongs to
// Bizhost rather than like a stray confirmation.
import AuthLayout from '../../../shared/ui/AuthLayout';
import StatusMark, { type Mark } from './StatusMark';
import { formatMoney } from '../../invoice/invoice';
import { usePaymentResult } from '../application/paymentResultQueries';
import { type PaymentResult as PaymentResultData } from '../payment';

/**
 * Where "naar het portaal" leads, and it has to work for two readers without
 * this page knowing which one it has.
 *
 * It does not have to know: an anonymous visitor — the normal case here — is
 * bounced to the sign-in screen by the UnAuthorized catch-all whatever the
 * path, and someone with a session follows it for real.
 *
 * `/login` was the first attempt and breaks the second reader: the authorized
 * route table does not know that path, so its catch-all sends a logged-in
 * customer to /not-found.
 *
 * Deliberately **not** the invoice itself. Whoever is reading this is not
 * necessarily the account that owns it — the invoice mail also reaches the
 * billing users of BHP-1187, who may have a portal account for an entirely
 * different customer, or none. The invoice page is customer-scoped, so for
 * all of them a direct link is a not-found, and this page cannot tell them
 * apart without an authenticated call it deliberately never makes.
 */
const PORTAL_PATH = '/dashboard';

interface Message {
  title: string;
  body: string;
  /** Tailwind text colour for the heading; the card itself stays neutral. */
  tone: string;
  mark: Mark;
}

export default function PaymentResult(): JSX.Element {
  const token = new URLSearchParams(window.location.search).get('token');
  const { data, isPending, isError } = usePaymentResult(token);

  const message = resolveMessage(token, data, isPending, isError);
  // A decimal string from the backend, but this page's whole job is not to
  // alarm anyone — and "€ NaN" under "your payment was received" would do
  // exactly that. Anything unreadable is simply left out.
  const amount = parseAmount(data?.amount);

  return (
    <AuthLayout
      title={
        <>
          <StatusMark mark={message.mark} />
          <span className={`block ${message.tone}`}>{message.title}</span>
        </>
      }
      footer={
        <a href={PORTAL_PATH} className="text-brand hover:underline">
          Naar het portaal
        </a>
      }
    >
      <p className="text-sm text-ink-muted">{message.body}</p>

      {/*
        Only ever the invoice number and the amount. Whoever holds this token
        just paid, but they are not an authenticated user: no customer
        details, no other invoices, no way into the portal's closed parts.
      */}
      {data?.invoiceNumber != null && (
        <dl className="mt-6 space-y-2 border-t border-surface-muted pt-4 text-sm">
          <div className="flex justify-between">
            <dt className="text-ink-muted">Factuurnummer</dt>
            <dd className="font-medium">{data.invoiceNumber}</dd>
          </div>
          {amount !== null && (
            <div className="flex justify-between">
              <dt className="text-ink-muted">
                {data?.checkoutUrl != null ? 'Openstaand bedrag' : 'Bedrag'}
              </dt>
              {/*
                EUR, because that is the only currency the payment link is
                ever created in (MolliePaymentLinkService). The result
                endpoint does not carry a currency, and inventing one here
                would be worse than the assumption.
              */}
              <dd className="font-medium">{formatMoney(amount, 'EUR')}</dd>
            </div>
          )}
        </dl>
      )}

      {/*
        The way back in, and the whole reason the backend returns this URL
        only after a failed attempt: the payment link stays valid, so the
        useful thing here is a button — not a sentence sending the customer
        back to their mail to find the one they already clicked.
      */}
      {data?.checkoutUrl != null && (
        <a
          href={data.checkoutUrl}
          className="mt-6 block w-full rounded-md bg-brand px-4 py-2 text-center font-medium text-ink-inverse transition-colors hover:bg-brand-dark"
        >
          Opnieuw betalen
        </a>
      )}
    </AuthLayout>
  );
}

/**
 * The copy, and the only place where a status becomes words.
 *
 * One rule governs all of it: **never suggest the payment failed unless we
 * know it did.** Someone who is told their payment went wrong pays again, and
 * then we owe them a refund and an apology. Everything we are unsure about —
 * a missing token, our own API being down, Mollie still thinking about it —
 * reads as "we do not know yet", never as "it went wrong".
 */
function resolveMessage(
  token: string | null,
  data: PaymentResultData | undefined,
  isPending: boolean,
  isError: boolean
): Message {
  if (token === null || token === '') {
    return expired();
  }

  if (isPending) {
    return {
      title: 'Even geduld',
      body: 'We halen de status van je betaling op.',
      tone: 'text-ink-muted',
      mark: 'info'
    };
  }

  // Our own API is unreachable. That says nothing about the payment, so it
  // must not read as if the payment failed.
  if (isError || data === undefined) {
    return {
      title: 'We kunnen de status nu niet ophalen',
      body: 'Je betaling is hierdoor niet mislukt. Probeer het zo nog eens, of kijk later in het portaal.',
      tone: 'text-ink-muted',
      mark: 'info'
    };
  }

  switch (data.status) {
    case 'paid':
      return {
        title: 'Bedankt, je betaling is ontvangen',
        body: 'De factuur staat bij ons als betaald genoteerd. Je hoeft verder niets te doen.',
        tone: 'text-brand-dark',
        mark: 'success'
      };

    case 'pending':
      return {
        title: 'Je betaling is nog niet bevestigd',
        body: 'Dat kan even duren, bijvoorbeeld bij een overboeking. Je krijgt bericht zodra we de betaling binnen hebben — betaal niet nog een keer.',
        tone: 'text-ink',
        mark: 'waiting'
      };

    case 'failed':
      return {
        title: 'De betaling is niet afgerond',
        body: 'Er is niets afgeschreven. Je kunt het hieronder opnieuw proberen.',
        tone: 'text-ink',
        mark: 'failure'
      };

    // An expired token, a tampered one, or a payment we cannot find. All the
    // same to whoever is reading: most likely an old tab, and nothing to be
    // alarmed about.
    case 'unknown':
    default:
      return expired();
  }
}

function parseAmount(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const amount = Number(raw);

  return Number.isFinite(amount) ? amount : null;
}

function expired(): Message {
  return {
    title: 'Deze pagina is verlopen',
    body: 'De link naar dit betaalresultaat is maar korte tijd geldig. Log in op het portaal om te zien of de factuur betaald is.',
    tone: 'text-ink-muted',
    mark: 'info'
  };
}
